import React, { useState } from 'react';
import cx from 'classnames';
import { observer } from 'mobx-react-lite';
import { Modal } from '@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal';
import { WishListRemove } from './ModalContent/WishListRemove';
import { WishListSelector } from './ModalContent/WishListSelector';
import { WishListVariants } from './ModalContent/WishListVariants';
import WishListsStore from '../../stores/WishListsStore';
import styles from './WishListModal.module.scss';
import { WISHLIST_URL_WITH_CREATE_WL_MODAL } from '../../helpers/redirect/constants';
import { EmailMeModalContent } from '../EmailMeModalContent/EmailMeModalContent';
import { runInAction } from 'mobx';
import { getProductDetails } from '../../helpers/productDetails/productDetails';
import {
    trackEvent as AdobetrackEvent,
    AddToWishlist as AdobeAddToWishlist,
} from '@JohnLewisPartnership/jl-analytics-event-stream';
import { getProductNotificationDetails } from '../../helpers/quickViewProductDetails/GetProductNotificationDetails';

interface WishlistModalProps {
    wishListStore: WishListsStore;
}

export const WishListModal: React.FC<WishlistModalProps> = observer(({ wishListStore }) => {
    const [store] = useState(wishListStore);
    const { currentModal, reset } = store;

    let header = '';
    switch (currentModal) {
        case 'wishlist':
            header = 'Select a list';
            break;
        case 'variant':
            // eslint-disable-next-line no-case-declarations
            const hasSizes = (store.currentProduct?.sizes?.length ?? 0) > 0;
            // eslint-disable-next-line no-case-declarations
            const hasColours = (store.currentProduct?.colours?.length ?? 0) > 0;

            if (hasColours && hasSizes) {
                header = 'Confirm size and colour';
            } else {
                if (hasSizes) {
                    header = 'Confirm size';
                } else {
                    header = 'Confirm colour';
                }
            }
            break;
        case 'remove':
            header = 'Remove from list';
            break;
        case 'email':
            header = 'Email when in stock';
            break;
    }

    function onBack(): void {
        store.setModal('variant');
    }

    function onClose(): void {
        if (store.currentModal === 'email' && store.emailModalStore) {
            runInAction(() => {
                if (store.emailModalStore) {
                    store.emailModalStore.selectedProduct = undefined;
                    store.emailModalStore.customerNotifiableEvents = undefined;
                    store.emailModalStore.customerEmail = '';
                    store.emailModalStore.emailFieldTouched = false;
                }
            });
        }
        reset();
    }
    async function onConfirm(): Promise<void> {
        store.dispatchGlobalEvent(
            'add.wishlist',
            store.getActionType(),
            'add to wishlist',
            store.currentProduct,
            'select a list',
        );

        const itemPrice = store.getItemPrice(store.currentProduct);

        // new analytic event
        AdobetrackEvent(AdobeAddToWishlist, {
            triggeringImplementationId: 'add-to-wishlist-component',
            originatingImplementationId: 'add-to-wishlist-component',
            type: store.mode === 'icon-placeholder' ? 'plp' : 'pdp',
            modalStep: 'select a list',
            products: store.currentProduct
                ? [
                      {
                          price: {
                              now: itemPrice || 0,
                          },
                          id: store.currentProduct.id,
                          sku: store.currentProduct.skuId,
                          quantity: 1,
                      },
                  ]
                : [],

            page: {
                name: window.jlData?.page?.pageInfo?.pageName,
                type: window.jlData?.page?.pageInfo?.pageType,
            },
            basket: {
                products: [],
                subTotal: 0,
                productCount: 0,
                quantity: 0,
            },
        });
        for (const wishListId of store.selectedWishListIds) {
            const isMultipleWishlists = store.selectedWishListIds.length > 1;
            await store.addToWishList(store.currentProductSkuId, wishListId, isMultipleWishlists);
        }

        if (!store.hasError) {
            if (
                (store.mode === 'icon-placeholder-pdp' || store.mode === 'icon-placeholder-basket') &&
                store.variantChanged
            ) {
                store.updateURL();
            }

            const productDetails = store.parseProductDetails(getProductDetails());

            const productId = productDetails?.productId as string;
            const productNotificationDetail = await store.handledAPIResult(() =>
                getProductNotificationDetails(productId, store.currentProductSkuId),
            );

            const customerNotifiableEvents =
                productNotificationDetail?.customerNotifiableEvents as CustomerNotifiableEvents;

            const allNotifiableEventTypesInPriorityOrder: string[] = [
                'backInStock',
                'onRelease',
                'onPreorderLaunch',
                'newlyInStock',
            ];

            const notifiableEvents = allNotifiableEventTypesInPriorityOrder.find(
                (eventType) =>
                    customerNotifiableEvents && customerNotifiableEvents[eventType as keyof CustomerNotifiableEvents],
            );

            if (store.mode === 'icon-placeholder-pdp' && productDetails?.isOutOfStock && notifiableEvents) {
                store.setModal('email');
            } else {
                onClose();
            }
        }
    }

    const selectWishListModal = {
        isSelectWishListModal: currentModal === 'wishlist',
        buttonCaption: !store.selectedWishListIds.length ? 'Choose a list first' : 'Confirm',
        buttonProps: {
            disabled: !store.selectedWishListIds.length || store.isCallingAPI,
            submitting: store.isCallingAPI,
        },
    };

    const productMetadata = store.parseCurrentProductMetadata();

    const handleCreateWishList = (): void => {
        const skuId = store.currentProduct?.skuId;
        const fabricSku = productMetadata?.asafProperties?.fabricSku;
        const legType = productMetadata?.asafProperties?.legType;

        window.location.href = `${WISHLIST_URL_WITH_CREATE_WL_MODAL}${
            skuId ? `&skuId=${skuId}` : ''
        }${fabricSku ? `&fabricSku=${fabricSku}` : ''}${legType ? `&legType=${legType}` : ''}`;
    };

    return (
        <Modal
            height="hug"
            primaryButtonText={selectWishListModal.isSelectWishListModal ? selectWishListModal.buttonCaption : ''}
            primaryButtonProps={selectWishListModal.isSelectWishListModal ? selectWishListModal.buttonProps : undefined}
            underlinedButtonText={
                selectWishListModal.isSelectWishListModal && wishListStore.wishLists.length < 5
                    ? 'Create a new wish list'
                    : ''
            }
            variant="standard"
            data-testid="wishlist-modal"
            className={cx(styles.modal, currentModal)}
            isOpen={!!currentModal}
            title={header}
            onBackButtonClick={onBack}
            onClose={onClose}
            backButton={
                currentModal == 'wishlist' && store.currentProduct?.isMultiSku && store.mode !== 'icon-placeholder-pdp'
            }
            onPrimaryButtonClick={selectWishListModal.isSelectWishListModal ? onConfirm : undefined}
            onUnderlinedButtonClick={
                selectWishListModal.isSelectWishListModal && wishListStore.wishLists.length < 5
                    ? handleCreateWishList
                    : undefined
            }
            headerType="standard"
            width="small"
            footer={selectWishListModal.isSelectWishListModal}
            footerLayout="inline"
        >
            {currentModal === 'variant' && <WishListVariants store={store} onClose={onClose} />}

            {selectWishListModal.isSelectWishListModal && <WishListSelector store={store} />}

            {currentModal === 'remove' && <WishListRemove store={store} onClose={onClose} />}

            {currentModal === 'email' && store.emailModalStore && store.currentProduct?.useOutOfStockButton && (
                <EmailMeModalContent
                    emailModalStore={store.emailModalStore}
                    onCancel={onClose}
                    onNotify={async (): Promise<void> => {
                        await store.emailModalStore?.emailMe();
                        onClose();
                    }}
                />
            )}
        </Modal>
    );
});
